import { supabase } from "@/integrations/supabase/client";
import type { LearningEngineDecision } from "./types";
import { SAFE_LOCKED_DECISION } from "./engineRules";
import { resolveNextLearningAction } from "./resolveNextLearningAction";

/**
 * CANONICAL PRODUCTION LEARNER DECISION PATH.
 *
 * `engine_decide` is the governed decision entry point: it stamps the active
 * policy version, records the diagnosis, the learning objective, the resolved
 * intervention modality, the alternatives considered and the evidence the
 * decision rested on, then returns the route. Route authority itself still
 * comes from `resolve_next_learning_action` inside the same transaction, so
 * prerequisites, release gating and the daily lesson cap are unchanged.
 *
 * Fallback contract (safe, never inventive):
 *  - governed call succeeds  -> use it, carry decision_id/policy/diagnosis.
 *  - governed call fails     -> fall back to the plain route resolver, marked
 *                               as a degraded decision so telemetry can see it.
 *  - both fail               -> SAFE_LOCKED_DECISION (never an unlocked guess).
 */
export interface GovernedLearningDecision extends LearningEngineDecision {
  decision_id: string | null;
  policy_version: string | null;
  competency_key: string | null;
  diagnosis: string | null;
  objective: string | null;
  modality: string | null;
  governed: boolean;
}

const DEGRADED_FIELDS = {
  decision_id: null,
  policy_version: null,
  competency_key: null,
  diagnosis: null,
  objective: null,
  modality: null,
  governed: false,
} as const;

type EngineEnvelope = {
  decision_id?: string;
  policy_version?: string;
  competency_key?: string;
  diagnosis?: string;
  objective?: string;
  resolution?: { modality?: string; alternatives?: unknown } | null;
  evidence_ids?: string[];
  safe_locked?: boolean;
  reason?: string;
  route?: {
    action?: LearningEngineDecision["action"];
    target_route?: string;
    target_lesson_id?: string | null;
    target_module_key?: string | null;
    is_locked?: boolean;
    audit_code?: string;
  };
};

export function normalizeGovernedDecision(
  envelope: EngineEnvelope,
  routeDecision: LearningEngineDecision,
): GovernedLearningDecision {
  // Kill switch: no active governed policy means no adaptation at all.
  if (envelope.safe_locked) {
    return {
      ...SAFE_LOCKED_DECISION,
      reason: envelope.reason ?? SAFE_LOCKED_DECISION.reason,
      audit_code: "SAFE_LOCK_NO_ACTIVE_POLICY",
      ...DEGRADED_FIELDS,
    };
  }
  const route = envelope.route ?? {};
  const isLocked = route.is_locked ?? routeDecision.is_locked;
  // A governed envelope that carries its own route is healthy. The placeholder
  // route decision is the safe-lock record, so its "could not verify
  // progression eligibility" copy must never surface on an unlocked decision.
  const placeholderReason =
    routeDecision.reason === SAFE_LOCKED_DECISION.reason && !isLocked
      ? "Your next step is ready."
      : routeDecision.reason;
  return {
    ...routeDecision,
    action: route.action ?? routeDecision.action,
    reason: envelope.reason ?? placeholderReason,
    lock_reason: isLocked ? routeDecision.lock_reason : null,

    target_route: route.target_route ?? routeDecision.target_route,
    target_lesson_id: route.target_lesson_id ?? routeDecision.target_lesson_id,
    target_module_key: route.target_module_key ?? routeDecision.target_module_key,
    is_locked: isLocked,
    audit_code: route.audit_code ?? routeDecision.audit_code,

    metadata: {
      ...routeDecision.metadata,
      evidence_ids: envelope.evidence_ids ?? [],
      alternatives: envelope.resolution?.alternatives ?? [],
    },
    decision_id: envelope.decision_id ?? null,
    policy_version: envelope.policy_version ?? null,
    competency_key: envelope.competency_key ?? null,
    diagnosis: envelope.diagnosis ?? null,
    objective: envelope.objective ?? null,
    modality: envelope.resolution?.modality ?? null,
    governed: true,
  };
}

export async function decideNextLearningAction(): Promise<GovernedLearningDecision> {
  const { data: userRes } = await supabase.auth.getUser();
  if (!userRes?.user) return { ...SAFE_LOCKED_DECISION, ...DEGRADED_FIELDS };

  const { data, error } = await (supabase as never as {
    rpc: (n: string, a?: unknown) => Promise<{ data: unknown; error: { message: string } | null }>;
  }).rpc("engine_decide", {});

  if (error || !data || typeof data !== "object") {
    // Safe fallback only — the route resolver is the same authority the
    // governed path wraps, so gating is never weakened by degrading here.
    if (error) console.error("[learning-engine] engine_decide failed", error.message);
    const fallback = await resolveNextLearningAction();
    return {
      ...fallback,
      ...DEGRADED_FIELDS,
      metadata: { ...fallback.metadata, degraded: true, degradedReason: error?.message ?? "empty_envelope" },
    };
  }

  const envelope = data as EngineEnvelope;
  // The governed envelope already carries route authority; only re-resolve when
  // it somehow arrived without a route, so a normal turn costs one round trip.
  const routeDecision = envelope.route?.target_route
    ? SAFE_LOCKED_DECISION
    : await resolveNextLearningAction();
  return normalizeGovernedDecision(envelope, routeDecision);
}
